import * as Sentry from '@sentry/react';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import AppLayout from '@/app/layouts/App.layout';
import ProtectedRoute from '@/app/routes/Protected.route';
import UnauthorizedOnlyRoute from '@/app/routes/UnauthorizedOnly.route';
import BottomSheetPage from '@/pages/bottom-sheet';
import HomePage from '@/pages/home';
import LoginPage from '@/pages/login';
import OnboardingPage from '@/pages/onboarding';
import { StationDetailPageSkeleton } from '@/pages/station-detail';
import StationListPage from '@/pages/station-list';
import GlobalErrorBoundary from '@/shared/ui/global-error-boundary';
import PageErrorBoundary from '@/shared/ui/page-error-boundary';

const SearchPage = lazy(() => import('@/pages/search'));
const StationInfoPage = lazy(() =>
	import('@/pages/station-detail').then((m) => ({
		default: m.StationInfoPage,
	})),
);
const StationDetailPage = lazy(() => import('@/pages/station-detail'));
const TermsPage = lazy(() => import('@/pages/terms'));
const PrivacyPolicyPage = lazy(() => import('@/pages/privacy-policy'));
const ProfilePage = lazy(() => import('@/pages/profile'));
const ProfileEditPage = lazy(() => import('@/pages/profile-edit'));
const FavoritesPage = lazy(() => import('@/pages/favorites'));
const CarSearchPage = lazy(() => import('@/pages/car-search'));
const CarRegistPage = lazy(() => import('@/pages/car-regist'));
const CarRegistIntroPage = lazy(() =>
	import('@/pages/car-regist').then((m) => ({ default: m.CarRegistIntroPage })),
);
const CarEditPage = lazy(() => import('@/pages/car-edit'));

const sentryCreateBrowserRouter =
	Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const Router = sentryCreateBrowserRouter(
	[
		{
			element: (
				<GlobalErrorBoundary>
					<AppLayout />
				</GlobalErrorBoundary>
			),
			children: [
				{
					path: '/',
					element: <HomePage />,
					children: [
						{ index: true, element: <Navigate to="stations" replace /> },
						{
							element: <BottomSheetPage />,
							children: [
								{
									path: 'stations',
									element: (
										<PageErrorBoundary>
											<StationListPage />
										</PageErrorBoundary>
									),
								},
								{
									path: 'stations/:stationId',
									element: (
										<PageErrorBoundary>
											<Suspense fallback={<StationDetailPageSkeleton />}>
												<StationInfoPage />
											</Suspense>
										</PageErrorBoundary>
									),
								},
							],
						},
					],
				},
				{
					path: 'search',
					element: (
						<PageErrorBoundary>
							<SearchPage />
						</PageErrorBoundary>
					),
				},
				{
					path: 'stations/:stationId/detail',
					element: (
						<PageErrorBoundary>
							<Suspense fallback={<StationDetailPageSkeleton />}>
								<StationDetailPage />
							</Suspense>
						</PageErrorBoundary>
					),
				},
				{
					element: <UnauthorizedOnlyRoute />,
					children: [
						{
							path: 'onboarding',
							element: (
								<PageErrorBoundary>
									<OnboardingPage />
								</PageErrorBoundary>
							),
						},
						{
							path: 'login',
							element: (
								<PageErrorBoundary>
									<LoginPage />
								</PageErrorBoundary>
							),
						},
					],
				},
				{
					element: <ProtectedRoute />,
					children: [
						{
							path: 'favorites',
							element: (
								<PageErrorBoundary>
									<FavoritesPage />
								</PageErrorBoundary>
							),
						},
						{
							path: 'profile',
							element: (
								<PageErrorBoundary>
									<ProfilePage />
								</PageErrorBoundary>
							),
						},
						{
							path: 'profile/edit',
							element: (
								<PageErrorBoundary>
									<ProfileEditPage />
								</PageErrorBoundary>
							),
						},
						{
							path: 'profile/regist-car-intro',
							element: (
								<PageErrorBoundary>
									<CarRegistIntroPage />
								</PageErrorBoundary>
							),
						},
						{
							path: 'profile/regist-car',
							element: (
								<PageErrorBoundary>
									<CarRegistPage />
								</PageErrorBoundary>
							),
						},
						{
							path: 'profile/edit-car',
							element: (
								<PageErrorBoundary>
									<CarEditPage />
								</PageErrorBoundary>
							),
						},
						{
							path: 'profile/search-car',
							element: (
								<PageErrorBoundary>
									<CarSearchPage />
								</PageErrorBoundary>
							),
						},
						{
							path: 'profile/privacy-policy',
							element: (
								<PageErrorBoundary>
									<PrivacyPolicyPage />
								</PageErrorBoundary>
							),
						},
						{
							path: 'profile/terms',
							element: (
								<PageErrorBoundary>
									<TermsPage />
								</PageErrorBoundary>
							),
						},
					],
				},
			],
		},
	],
	{ basename: import.meta.env.BASE_URL },
);

export default Router;
